<template>
  <v-card>
    <v-card-title primary-title class="pb-0">
      <div class="headline">
        Fee configuration for partner {{ partnerDetails.Name }}
      </div>
    </v-card-title>

    <v-card-text>
      <v-form ref="partnerFeeForm">
        <v-container>
          <v-layout column>
            <v-flex>
              <v-text-field
                v-model="partnerFee.AmountExVat"
                label="Amount excl VAT"
                outline
                type="number"
                min="0"
                prefix="€"
                :rules="amountRules"
                @input="onPartnerFeeChange"
              ></v-text-field>
            </v-flex>

            <v-flex>
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="partnerFee.StartDate"
                    :label="$translate('STARTDATE')"
                    append-icon="event"
                    readonly
                    outline
                    :rules="requiredRules"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDateYMD"
                  first-day-of-week="1"
                  color="primary"
                  @input="onStartDateChange"
                  no-title
                />
              </v-menu>
            </v-flex>

            <v-flex>
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-layout column>
                    <v-text-field
                      v-model="partnerFee.EndDate"
                      :label="$translate('ENDDATE')"
                      append-icon="event"
                      readonly
                      outline
                      persistent-hint
                      hide-details
                      v-on="on"
                    ></v-text-field>
                    <div
                      class="caption grey--text text--darken-2 mt-1 px-2"
                      v-html="endDateHint"
                    ></div>
                  </v-layout>
                </template>
                <v-date-picker
                  v-model="endDateYMD"
                  first-day-of-week="1"
                  color="primary"
                  :min="startDateYMD"
                  @input="onEndDateChange"
                  no-title
                />
              </v-menu>
            </v-flex>

            <v-flex>
              <v-textarea
                v-model="partnerFee.Description"
                outline
                counter
                maxlength="1024"
                rows="3"
                :label="$translate('DESCRIPTION')"
                @input="onPartnerFeeChange"
              ></v-textarea>
            </v-flex>
          </v-layout>
        </v-container>
      </v-form>
    </v-card-text>

    <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="secondary"
          class="text-capitalize"
          @click="onCancelBtnClick"
        >{{ $translate('CANCEL') }}</v-btn>
        <v-btn
          color="primary"
          class="text-capitalize"
          :loading="saveBtnLoading"
          @click="onSaveBtnClick"
        >{{ $translate('SAVE') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import * as dssRequest from '@/api/dssRequest';
import { dateFromDMYToYMD, dateFromYMDToDMY } from '@/shared/utility';
import { requiredRules } from '@/shared/validations_rules';

export default {
  name: 'Financial_PartnerFeeEdit',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    partnerDetails: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: [
    // Emits when the partner fee changed
    'input',
    // Emits when the cancel button is clicked
    'cancel',
    // Emits when the partner fee is saved successfully
    'save',
  ],
  watch: {
    value() {
      this.init();
    },
  },
  data() {
    return {
      partnerFee: {},
      startDateMenu: false,
      endDateMenu: false,
      startDateYMD: '',
      endDateYMD: '',
      requiredRules,
      amountRules: [
        ...requiredRules,
        (v) => (v >= 0) || this.$translate('Only positive number is allowed'),
      ],
      saveBtnLoading: false,
      endDateHint: this.getEndDateHint(),
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      if (this.value != null) {
        this.partnerFee = { ...this.value };
      } else {
        this.partnerFee = {};
      }

      this.startDateYMD = dateFromDMYToYMD(this.partnerFee.StartDate);
      this.endDateYMD = dateFromDMYToYMD(this.partnerFee.EndDate);
    },
    getEndDateHint() {
      return '- For indefinite monthly payment, leave end date empty.<br>- For monthly payment within a period, choose the end date of the payment.<br>- For one time payment, choose an end date that is the same as the start date.';
    },
    onStartDateChange() {
      this.startDateMenu = false;
      this.partnerFee.StartDate = dateFromYMDToDMY(this.startDateYMD);
      this.onPartnerFeeChange();
    },
    onEndDateChange() {
      this.endDateMenu = false;
      this.partnerFee.EndDate = dateFromYMDToDMY(this.endDateYMD);
      this.onPartnerFeeChange();
    },
    onPartnerFeeChange() {
      this.$emit('input', { ...this.partnerFee });
    },
    onCancelBtnClick() {
      this.$emit('cancel');
    },
    onSaveBtnClick() {
      if (!this.$refs.partnerFeeForm.validate()) {
        return;
      }

      this.savePartnerFee();
    },
    savePartnerFee() {
      const payload = { ...this.getPaylodForSavePartnerFee() };
      this.saveBtnLoading = true;

      dssRequest.doRequest(
        (response) => {
          console.log('the call for savePartnerFee was successful', response);
          this.saveBtnLoading = false;
          this.$emit('save');
        },
        (error) => {
          console.log('the call for savePartnerFee was not successful', error);
          this.saveBtnLoading = false;
        },
        'savePartnerFee',
        'LocationsNode_FMM',
        payload,
      );
    },
    getPaylodForSavePartnerFee() {
      const payload = { ...this.partnerFee };

      // Other fields should be validated by form rules
      if (payload.PartnerId == null) {
        payload.PartnerId = this.partnerDetails.PartnerId;
      }

      if (payload.Description == null) {
        payload.Description = '';
      }

      return payload;
    },
  },
};
</script>
